/**
 * ImageResultsGrid Component
 * 
 * Displays search results in a responsive grid with selection state.
 * Includes pagination, loading states, and error handling.
 */

import React from 'react';
import {
  Box,
  ImageList,
  ImageListItem,
  Typography,
  Pagination,
  CircularProgress,
  Checkbox,
  Alert,
  Skeleton,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  BrokenImage as BrokenImageIcon,
} from '@mui/icons-material';
import type { ImageResultsGridProps, Image } from './types';
import { THEME_COLORS } from './constants';

export const ImageResultsGrid: React.FC<ImageResultsGridProps> = ({
  images,
  selectedIds,
  onToggleSelect,
  selectionDisabled,
  isLoading,
  currentPage,
  totalPages,
  onPageChange,
  error,
}) => {
  // Loading skeleton
  if (isLoading && images.length === 0) {
    return (
      <Box sx={{ mt: 3 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
            gap: 2,
          }}
        >
          {Array.from({ length: 8 }).map((_, index) => (
            <Skeleton
              key={index}
              variant="rectangular"
              sx={{
                width: '100%',
                paddingTop: '100%',
                borderRadius: 1,
                bgcolor: THEME_COLORS.border,
              }}
            />
          ))}
        </Box>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ mt: 3 }}>
        <Alert
          severity="error"
          sx={{
            bgcolor: 'rgba(239, 68, 68, 0.1)',
            color: THEME_COLORS.error,
            border: `1px solid ${THEME_COLORS.error}`,
            '& .MuiAlert-icon': {
              color: THEME_COLORS.error,
            },
          }}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  // Empty state
  if (!isLoading && images.length === 0) {
    return (
      <Box
        sx={{
          mt: 3,
          py: 6,
          textAlign: 'center',
          color: THEME_COLORS.textSecondary,
        }}
      >
        <BrokenImageIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
        <Typography variant="h6">No images found</Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Try different keywords or change the image source
        </Typography>
      </Box>
    );
  }

  const handleImageClick = (image: Image) => {
    const isSelected = selectedIds.has(image.id);
    // Only allow toggle if not at max or if deselecting
    if (!selectionDisabled || isSelected) {
      onToggleSelect(image);
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      {/* Results Grid */}
      <ImageList
        sx={{
          gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr)) !important',
          gap: '12px !important',
        }}
        gap={12}
      >
        {images.map((image) => {
          const isSelected = selectedIds.has(image.id);
          const canSelect = !selectionDisabled || isSelected;

          return (
            <ImageListItem
              key={image.id}
              onClick={() => handleImageClick(image)}
              sx={{
                cursor: canSelect ? 'pointer' : 'not-allowed',
                position: 'relative',
                borderRadius: 1,
                overflow: 'hidden',
                border: isSelected
                  ? `3px solid ${THEME_COLORS.accent}`
                  : `1px solid ${THEME_COLORS.border}`,
                transition: 'all 0.2s ease',
                opacity: canSelect ? 1 : 0.6,
                '&:hover': canSelect
                  ? {
                      borderColor: THEME_COLORS.accent,
                      transform: 'scale(1.02)',
                    }
                  : {},
              }}
            >
              <img
                src={image.thumbnail_url || image.preview_url || image.url}
                alt={image.tags?.join(', ') || 'Image'}
                loading="lazy"
                style={{
                  width: '100%',
                  height: 150,
                  objectFit: 'cover',
                }}
                onError={(e) => {
                  // Fallback for broken images
                  (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="150" height="150"><rect fill="%23333" width="150" height="150"/><text fill="%23666" x="50%" y="50%" dominant-baseline="middle" text-anchor="middle">No Image</text></svg>';
                }}
              />

              {/* Selection Checkbox Overlay */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: isSelected ? THEME_COLORS.accent : 'rgba(0,0,0,0.5)',
                  borderRadius: '50%',
                  width: 28,
                  height: 28,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                }}
              >
                {isSelected ? (
                  <CheckCircleIcon sx={{ color: 'white', fontSize: 20 }} />
                ) : (
                  <Checkbox
                    checked={false}
                    disabled={!canSelect}
                    sx={{
                      color: 'white',
                      p: 0,
                      '& .MuiSvgIcon-root': { fontSize: 20 },
                    }}
                  />
                )}
              </Box>

              {/* Author Attribution */}
              {image.author && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    bgcolor: 'rgba(0,0,0,0.7)',
                    px: 1,
                    py: 0.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'white',
                      fontSize: '0.65rem',
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    📷 {image.author}
                  </Typography>
                </Box>
              )}
            </ImageListItem>
          );
        })}
      </ImageList>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 3,
          }}
        >
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, page) => onPageChange(page)}
            disabled={isLoading}
            sx={{
              '& .MuiPaginationItem-root': {
                color: THEME_COLORS.textSecondary,
                borderColor: THEME_COLORS.border,
                '&:hover': {
                  bgcolor: THEME_COLORS.border,
                },
                '&.Mui-selected': {
                  bgcolor: THEME_COLORS.accent,
                  color: THEME_COLORS.background,
                  '&:hover': {
                    bgcolor: THEME_COLORS.accentHover,
                  },
                },
              },
            }}
          />
        </Box>
      )}

      {/* Loading overlay for page changes */}
      {isLoading && images.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 2,
          }}
        >
          <CircularProgress size={24} sx={{ color: THEME_COLORS.accent }} />
        </Box>
      )}
    </Box>
  );
};

export default ImageResultsGrid;

